import React from 'react';
import { FormattedMessage } from 'react-intl';
import { rem } from 'polished';
import styled from 'styled-components';

import BasicModal from 'components/GenericModal/BasicModal';
import GenericText from 'components/GenericText/GenericText';
import { GenericTitle, TitleMargin } from 'components/GenericTitle/GenericTitle';

const List = styled.ul`
  margin-top: ${rem(5)};
  margin-left: 0.9em;
  list-style: disc;

  li {
    color: ${({ theme }) => theme.colors.textDark};
  }

  li:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

const BrowserLink = styled.a`
  color: ${({ theme }) => theme.colors.fountainBlue};
`;

const Text = styled(GenericText)`
  color: ${({ theme }) => theme.colors.textDark};
`;

enum OS {
  Windows,
  MacOS,
  Android,
  Linux,
  iOS,
}

enum Browsers {
  GoogleChrome = 'Google Chrome',
  Firefox = 'Mozilla Firefox',
  Safari = 'Apple Safari',
  MicrosoftEdge = 'Microsoft Edge',
}

const BrowserLinks = {
  [Browsers.GoogleChrome]: 'https://www.google.com/intl/en_in/chrome/',
  [Browsers.Firefox]: 'https://www.firefox.com/en-US/',
  [Browsers.MicrosoftEdge]: 'https://www.microsoft.com/en-us/edge/download',
  [Browsers.Safari]: 'https://www.apple.com/in/safari/',
};

const OSBrowsers = {
  [OS.Windows]: [Browsers.GoogleChrome, Browsers.Firefox, Browsers.MicrosoftEdge],
  [OS.MacOS]: [Browsers.GoogleChrome, Browsers.Firefox, Browsers.MicrosoftEdge, Browsers.Safari],
  [OS.Linux]: [Browsers.GoogleChrome, Browsers.Firefox],
  [OS.Android]: [Browsers.GoogleChrome],
  [OS.iOS]: [Browsers.GoogleChrome, Browsers.Safari],
};

function getOperatingSystem() {
  const { userAgent, platform } = navigator;

  if (/Windows NT 10/.test(userAgent)) return OS.Windows;
  if (/Windows NT 6.3/.test(userAgent)) return OS.Windows;
  if (/Windows NT 6.2/.test(userAgent)) return OS.Windows;
  if (/Windows NT 6.1/.test(userAgent)) return OS.Windows;
  if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(platform)) return OS.MacOS;
  if (/iPhone/.test(userAgent)) return OS.iOS;
  if (/iPad/.test(userAgent)) return OS.iOS;
  if (/Android/.test(userAgent)) return OS.Android;
  if (/Linux/.test(platform)) return OS.Linux;

  return OS.Linux;
}

interface Props {
  isOpen: boolean;
  closeModal: () => void;
}
function BrowserWarningModal({ isOpen, closeModal }: Props) {
  const operatingSystem = getOperatingSystem();

  return (
    <BasicModal
      width={rem(690)}
      onClose={closeModal}
      shouldCloseOnOverlayClick={false}
      isOpen={isOpen}
    >
      <BasicModal.Content>
        <GenericTitle>
          <FormattedMessage id="dashboard.shared.browserWarning.title" />
        </GenericTitle>
        <Text hasMarginTopSm>
          <FormattedMessage id="dashboard.shared.browserWarning.description" />
        </Text>

        <GenericTitle as="h3" isMedium isBold marginTop={TitleMargin.Sm}>
          <FormattedMessage id="dashboard.shared.browserWarning.notWorkTitle" />
        </GenericTitle>

        <List>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point1" />
          </li>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point2" />
          </li>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point3" />
          </li>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point4" />
          </li>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point5" />
          </li>
          <li>
            <FormattedMessage id="dashboard.shared.browserWarning.notWorkList.point6" />
          </li>
        </List>

        <GenericTitle as="h3" isMedium isBold marginTop={TitleMargin.Sm}>
          <FormattedMessage id="dashboard.shared.browserWarning.recommendedBrowserTitle" />
        </GenericTitle>

        <Text hasMarginTopSm>
          <FormattedMessage id="dashboard.shared.browserWarning.recommendedBrowserDescription" />
        </Text>

        <List>
          {OSBrowsers[operatingSystem].map(item => {
            return (
              <li key={item}>
                <BrowserLink href={BrowserLinks[item]} target="_blank">
                  {item}
                </BrowserLink>
              </li>
            );
          })}
        </List>

        <Text isBold hasMarginBottomMd hasMarginTopMd isTextMedium>
          <FormattedMessage id="dashboard.shared.browserWarning.recommendedBrowserNote" />
        </Text>
      </BasicModal.Content>
    </BasicModal>
  );
}

export default BrowserWarningModal;
